import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Clock,
  FileCheck2,
  FileSignature,
  Fingerprint,
  History,
  IdCard,
  KeyRound,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  QrCode,
  ScanSearch,
  Send,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  UploadCloud,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import loginBanner from "../assets/eSign_login_banner.png";
import { BrandLogo } from "../components/BrandLogo";
import { useAuth } from "../auth/OAuthContext";

const NAV_SECTIONS = [
  { id: "workflow", label: "Workflow" },
  { id: "roles", label: "Clinical Roles" },
  { id: "verification", label: "Trust & Verification" },
  { id: "security", label: "Security" },
] as const;

function smoothScrollTo(targetY: number, duration: number = 280) {
  const startY = window.scrollY || document.documentElement.scrollTop;
  const distance = targetY - startY;
  if (Math.abs(distance) < 2) return;

  const originalScrollBehavior = document.documentElement.style.scrollBehavior;
  document.documentElement.style.scrollBehavior = "auto";

  let startTime: number | null = null;

  function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  function step(currentTime: number) {
    if (!startTime) startTime = currentTime;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeOutCubic(progress);

    window.scrollTo({ top: startY + distance * eased, behavior: "instant" as ScrollBehavior });

    if (progress < 1) {
      window.requestAnimationFrame(step);
    } else {
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
    }
  }

  window.requestAnimationFrame(step);
}


export function LandingPage() {
  const { idToken, email, roles, loading } = useAuth();
  const navigate = useNavigate();
  const signedIn = Boolean(idToken);
  const role = roles[0] ?? "User";

  const [activeHeroTab, setActiveHeroTab] = useState<"doctor" | "nurse" | "verify">("doctor");
  const [sealSearchToken, setSealSearchToken] = useState("");
  const [activeSection, setActiveSection] = useState<string>("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const sectionIds = ["workflow", "roles", "verification", "security"];

    const updateActiveSection = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollY > 20);

      // If at the top hero section, clear active selection
      if (scrollY < 180) {
        setActiveSection("");
        return;
      }

      // If user reached the bottom of the page, activate the last section
      const windowHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;
      if (scrollY + windowHeight >= scrollHeight - 60) {
        setActiveSection("security");
        return;
      }

      // Trigger line at 35% of the viewport height (well below the fixed header)
      const triggerPoint = windowHeight * 0.35;

      let matchedSection = "";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerPoint && rect.bottom > triggerPoint) {
          matchedSection = id;
          break;
        }
      }

      // If between sections, fallback to the section currently closest above the trigger point
      if (!matchedSection) {
        let bestCandidate = "";
        let bestTop = -Infinity;
        for (const id of sectionIds) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.top <= triggerPoint && rect.top > bestTop) {
            bestTop = rect.top;
            bestCandidate = id;
          }
        }
        matchedSection = bestCandidate;
      }

      setActiveSection(matchedSection);
    };

    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection, { passive: true });
    updateActiveSection();
    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      const headerOffset = 85;
      const targetY = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset);
      smoothScrollTo(targetY, 280);
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    smoothScrollTo(0, 280);
    setActiveSection("");
    setMobileMenuOpen(false);
  };

  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const token = sealSearchToken.trim();
    if (token) {
      navigate(`/verify/${encodeURIComponent(token)}`);
    }
  };

  return (
    <main className="min-h-screen bg-surface-subtle text-text-main font-sans pt-18 sm:pt-20">
      {/* 1. Fixed Header (Always visible across entire landing page scroll) */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b ${
          isScrolled
            ? "bg-surface-card/95 backdrop-blur-xl border-surface-border shadow-xs"
            : "bg-surface-card/90 backdrop-blur-md border-surface-border/70"
        }`}
      >
        <div className="max-w-7xl mx-auto h-18 sm:h-20 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              onClick={scrollToTop}
              aria-label="MEATrix OrderFlow home"
              className="shrink-0 flex items-center group transition-transform hover:scale-[1.02]"
            >
              <BrandLogo className="h-9 sm:h-10 w-auto object-contain" />
            </Link>

            {/* Desktop Navigation with Active / Inactive State Spy */}
            <nav
              className="hidden md:flex items-center gap-1 p-1 rounded-full bg-surface-subtle border border-surface-border/80 shadow-2xs"
              aria-label="Section navigation"
            >
              {NAV_SECTIONS.map(({ id, label }) => {
                const isActive = activeSection === id;
                return (
                  <a
                    key={id}
                    href={`#${id}`}
                    onClick={(e) => scrollToSection(e, id)}
                    className={`relative px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ease-out flex items-center gap-1.5 cursor-pointer select-none ${
                      isActive
                        ? "bg-surface-card text-brand-600 shadow-xs border border-brand-200/90 scale-[1.02]"
                        : "text-text-muted hover:text-text-main hover:bg-surface-card/60 border border-transparent"
                    }`}
                    aria-current={isActive ? "true" : undefined}
                  >
                    <span
                      className={`inline-block rounded-full bg-brand-600 transition-all duration-200 ease-out ${
                        isActive
                          ? "w-1.5 h-1.5 opacity-100 scale-100 animate-pulse"
                          : "w-0 h-0 opacity-0 scale-0 -mr-1.5"
                      }`}
                      aria-hidden="true"
                    />
                    <span className="transition-colors duration-200 ease-out">{label}</span>
                  </a>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {!loading && (signedIn ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-200/60">
                  <div className="w-7 h-7 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs font-extrabold uppercase">
                    {email?.charAt(0) || "U"}
                  </div>
                  <div className="leading-tight max-w-40 text-left">
                    <span className="block text-xs font-bold text-text-main truncate">{email}</span>
                    <span className="text-[11px] text-text-dim font-semibold">{role}</span>
                  </div>
                </div>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 min-h-10 px-4 sm:px-5 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-xs sm:text-sm font-bold shadow-sm transition-all cursor-pointer"
                >
                  <LayoutDashboard size={16} aria-hidden="true" />
                  <span>Open workspace</span>
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-3">
                <Link
                  to="/login"
                  className="px-3.5 sm:px-5 min-h-10 inline-flex items-center rounded-full text-xs sm:text-sm font-bold text-brand-700 hover:bg-brand-50 transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="px-4 sm:px-5 min-h-10 inline-flex items-center gap-1.5 sm:gap-2 rounded-full text-xs sm:text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 shadow-sm transition-all"
                >
                  <span>Register with NPI</span>
                  <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            ))}

            {/* Mobile Hamburger Toggle Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="md:hidden p-2 rounded-xl text-text-muted hover:text-text-main hover:bg-surface-subtle transition-colors cursor-pointer"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Slide-down Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-surface-border bg-surface-card px-4 pt-3 pb-5 shadow-lg space-y-3">
            <div className="flex flex-col gap-1">
              {NAV_SECTIONS.map(({ id, label }) => {
                const isActive = activeSection === id;
                return (
                  <a
                    key={id}
                    href={`#${id}`}
                    onClick={(e) => scrollToSection(e, id)}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-150 ease-out flex items-center justify-between ${
                      isActive
                        ? "bg-brand-50 text-brand-600 border border-brand-200/60"
                        : "text-text-muted hover:text-text-main hover:bg-surface-subtle"
                    }`}
                  >
                    <span>{label}</span>
                    <span
                      className={`w-2 h-2 rounded-full bg-brand-600 transition-all duration-150 ease-out ${
                        isActive ? "opacity-100 scale-100" : "opacity-0 scale-0"
                      }`}
                    />
                  </a>
                );
              })}
            </div>
            {!signedIn && (
              <div className="pt-2 border-t border-surface-border flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full min-h-11 flex items-center justify-center rounded-xl text-sm font-bold text-brand-700 bg-brand-50 hover:bg-brand-100/60"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full min-h-11 flex items-center justify-center gap-2 rounded-xl text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 shadow-sm"
                >
                  <span>Register with NPI</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        )}
      </header>

      {/* 2. Hero Section with Rich Banner & Interactive Platform Showcase */}
      <section className="relative overflow-hidden bg-navy-900 text-white">
        <img
          src={loginBanner}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-35 mix-blend-luminosity"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-navy-900/70" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(110deg, rgba(7,28,64,0.98) 0%, rgba(7,28,64,0.92) 50%, rgba(1,38,153,0.75) 100%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute -right-36 -top-48 w-144 h-144 rounded-full border-16 border-white/5 pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Hero Left Content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-widest text-brand-200">
              <Sparkles size={14} className="text-brand-200" />
              <span>Next-Gen Clinical VNA Coordination</span>
            </div>

            <h1 className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight font-display">
              Review orders,{" "}
              <span className="text-accent-500">seal decisions,</span>{" "}
              and keep the trail intact.
            </h1>

            <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl font-normal">
              MEATrix OrderFlow connects nurses, attending physicians, and clinical organizations
              through NPI-driven routing, side-by-side order review, and tamper-evident digital seals.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={signedIn ? "/dashboard" : "/login"}
                className="inline-flex justify-center items-center gap-2.5 min-h-12 px-6 rounded-full bg-white text-brand-600 font-bold hover:bg-brand-50 transition-all shadow-md"
              >
                <span>{signedIn ? "Go to your workspace" : "Get started securely"}</span>
                <ArrowRight size={17} aria-hidden="true" />
              </Link>
              {!signedIn ? (
                <Link
                  to="/register"
                  className="inline-flex justify-center items-center gap-2 min-h-12 px-6 rounded-full border border-white/35 bg-white/10 hover:bg-white/15 text-white font-bold transition-colors"
                >
                  <IdCard size={18} />
                  <span>Register with NPI</span>
                </Link>
              ) : (
                <a
                  href="#verification"
                  className="inline-flex justify-center items-center gap-2 min-h-12 px-6 rounded-full border border-white/35 bg-white/10 hover:bg-white/15 text-white font-bold transition-colors"
                >
                  <ScanSearch size={18} />
                  <span>Verify document</span>
                </a>
              )}
            </div>

            {/* Quick Proof Pills */}
            <div className="mt-9 pt-7 border-t border-white/15 grid grid-cols-3 gap-3">
              <div>
                <strong className="block text-xl sm:text-2xl font-bold font-display text-white">100%</strong>
                <span className="text-xs text-white/70">Cryptographic audit trail</span>
              </div>
              <div>
                <strong className="block text-xl sm:text-2xl font-bold font-display text-white">10-Digit</strong>
                <span className="text-xs text-white/70">NPI-based direct routing</span>
              </div>
              <div>
                <strong className="block text-xl sm:text-2xl font-bold font-display text-white">Part 11</strong>
                <span className="text-xs text-white/70">Compliant electronic seals</span>
              </div>
            </div>
          </div>

          {/* Hero Right: Interactive App Workspace Preview */}
          <div className="lg:col-span-5 bg-surface-card text-text-main rounded-3xl border border-white/20 hero-card-shadow overflow-hidden">
            {/* Mock Header Tabs */}
            <div className="bg-surface-subtle border-b border-surface-border p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 p-1 bg-white border border-surface-border rounded-xl text-xs font-bold">
                <button
                  type="button"
                  onClick={() => setActiveHeroTab("doctor")}
                  className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    activeHeroTab === "doctor"
                      ? "bg-brand-600 text-white shadow-xs"
                      : "text-text-muted hover:text-text-main"
                  }`}
                >
                  Doctor Review
                </button>
                <button
                  type="button"
                  onClick={() => setActiveHeroTab("nurse")}
                  className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    activeHeroTab === "nurse"
                      ? "bg-brand-600 text-white shadow-xs"
                      : "text-text-muted hover:text-text-main"
                  }`}
                >
                  Nurse Intake
                </button>
                <button
                  type="button"
                  onClick={() => setActiveHeroTab("verify")}
                  className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    activeHeroTab === "verify"
                      ? "bg-brand-600 text-white shadow-xs"
                      : "text-text-muted hover:text-text-main"
                  }`}
                >
                  Digital Seal
                </button>
              </div>
              <div className="flex items-center gap-1.5 pr-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-text-dim">Live VNA</span>
              </div>
            </div>

            {/* Tab 1: Doctor Queue Preview */}
            {activeHeroTab === "doctor" && (
              <div className="p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-surface-border">
                  <div>
                    <span className="text-[11px] font-bold text-text-dim uppercase tracking-wider">Active Worklist</span>
                    <h3 className="text-lg font-bold font-display text-text-main">Order #ORD-84910</h3>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-bold border border-orange-200 flex items-center gap-1.5">
                    <Clock size={12} />
                    <span>Pending review</span>
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs bg-surface-subtle p-3 rounded-2xl border border-surface-border">
                  <div>
                    <span className="text-text-dim block">Patient reference</span>
                    <strong className="text-sm font-bold text-text-main">Jessica Brown</strong>
                  </div>
                  <div>
                    <span className="text-text-dim block">Target Org NPI</span>
                    <strong className="text-sm font-bold text-text-main">1841392810</strong>
                  </div>
                  <div>
                    <span className="text-text-dim block">Classification</span>
                    <strong className="text-xs font-bold text-brand-600 bg-brand-50 px-2 py-0.5 rounded-md inline-block mt-1">
                      Clinical Order
                    </strong>
                  </div>
                  <div>
                    <span className="text-text-dim block">Suggested HCPCS</span>
                    <strong className="text-xs font-bold text-slate-800 mt-1 inline-block">E0601 (CPAP)</strong>
                  </div>
                </div>

                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between text-xs text-text-muted">
                    <span className="font-semibold">Attached Order PDF</span>
                    <span className="text-emerald-700 font-bold flex items-center gap-1">
                      <Check size={13} /> Validated format
                    </span>
                  </div>
                  <div className="p-3 rounded-xl border border-brand-200 bg-brand-50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-brand-600 text-white flex items-center justify-center font-bold text-xs">
                        PDF
                      </div>
                      <div className="leading-tight">
                        <span className="text-xs font-bold text-text-main block truncate max-w-44">
                          physician_order_jbrown.pdf
                        </span>
                        <span className="text-[11px] text-text-dim">248 KB • 2 pages</span>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-brand-600">Preview &gt;</span>
                  </div>
                </div>

                <div className="pt-2 flex items-center gap-2">
                  <div className="flex-1 py-2 px-3 rounded-xl bg-emerald-600 text-white font-bold text-xs text-center flex items-center justify-center gap-1.5 shadow-xs">
                    <FileCheck2 size={15} />
                    <span>Approve & Sign</span>
                  </div>
                  <div className="py-2 px-3 rounded-xl bg-orange-50 text-orange-700 font-bold text-xs border border-orange-200 text-center">
                    Request fix
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Nurse Intake Dropzone Preview */}
            {activeHeroTab === "nurse" && (
              <div className="p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-surface-border">
                  <div>
                    <span className="text-[11px] font-bold text-text-dim uppercase tracking-wider">Fast Route Intake</span>
                    <h3 className="text-lg font-bold font-display text-text-main">Send New Document</h3>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200 flex items-center gap-1.5">
                    <BadgeCheck size={13} />
                    <span>Ready</span>
                  </span>
                </div>

                <div className="border-2 border-dashed border-brand-200 rounded-2xl p-6 text-center bg-slate-50">
                  <div className="w-12 h-12 mx-auto rounded-full bg-brand-50 text-brand-600 flex items-center justify-center mb-2">
                    <UploadCloud size={24} />
                  </div>
                  <strong className="block text-sm font-bold text-text-main">Drag order PDF here</strong>
                  <span className="text-xs text-text-dim mt-0.5 block">or select from local directory</span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-surface-subtle border border-surface-border">
                    <span className="text-text-dim">Recipient NPI</span>
                    <strong className="font-bold text-text-main">10-Digit Verified Org</strong>
                  </div>
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-surface-subtle border border-surface-border">
                    <span className="text-text-dim">Patient Tracking</span>
                    <strong className="font-bold text-text-main">Auto-Indexed</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Digital Seal Verification Preview */}
            {activeHeroTab === "verify" && (
              <div className="p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-surface-border">
                  <div>
                    <span className="text-[11px] font-bold text-text-dim uppercase tracking-wider">Public Verification</span>
                    <h3 className="text-lg font-bold font-display text-text-main">Seal #SL-99381</h3>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200 flex items-center gap-1.5">
                    <ShieldCheck size={14} />
                    <span>Cryptographically Valid</span>
                  </span>
                </div>

                <div className="space-y-2.5">
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                      <Fingerprint size={16} />
                    </div>
                    <div className="leading-tight text-xs">
                      <strong className="block text-text-main font-bold">SHA-256 PDF Fingerprint</strong>
                      <span className="text-emerald-700 font-semibold text-[11px]">Match verified • Zero alterations</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-brand-50 border border-brand-200 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-200 text-brand-600 flex items-center justify-center shrink-0">
                      <LockKeyhole size={15} />
                    </div>
                    <div className="leading-tight text-xs">
                      <strong className="block text-text-main font-bold">Physician Signature</strong>
                      <span className="text-brand-600 font-semibold text-[11px]">Signed with active NPI credentials</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-surface-subtle rounded-xl border border-surface-border text-[11px] text-text-muted flex items-center justify-between">
                  <span>Public audit token</span>
                  <span className="font-mono font-bold text-text-main">ea4f...9c10</span>
                </div>
              </div>
            )}

            {/* Bottom mini bar */}
            <div className="px-6 py-3 bg-brand-50 border-t border-surface-border flex items-center justify-between text-xs text-text-muted">
              <span className="font-semibold">End-to-end traceability</span>
              <span className="font-bold text-brand-600">Learn how it works &darr;</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Metrics & Key Highlights Bar */}
      <section className="border-b border-surface-border bg-surface-card" aria-label="Key highlights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-surface-card border border-surface-border card-shadow flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                <Send size={22} />
              </div>
              <div>
                <span className="text-2xl font-bold font-display text-text-main">10-Digit</span>
                <span className="block text-xs font-semibold text-text-dim mt-0.5">Automated NPI routing</span>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-surface-card border border-surface-border card-shadow flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-700 flex items-center justify-center shrink-0">
                <Clock size={22} />
              </div>
              <div>
                <span className="text-2xl font-bold font-display text-text-main">&lt; 3 Min</span>
                <span className="block text-xs font-semibold text-text-dim mt-0.5">Average review turnaround</span>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-surface-card border border-surface-border card-shadow flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                <ShieldCheck size={22} />
              </div>
              <div>
                <span className="text-2xl font-bold font-display text-text-main">SHA-256</span>
                <span className="block text-xs font-semibold text-text-dim mt-0.5">Cryptographic PDF seals</span>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-surface-card border border-surface-border card-shadow flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
                <History size={22} />
              </div>
              <div>
                <span className="text-2xl font-bold font-display text-text-main">100%</span>
                <span className="block text-xs font-semibold text-text-dim mt-0.5">Traceable revision log</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Complete 4-Step Clinical Workflow */}
      <section id="workflow" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div className="max-w-xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-600">End-to-End Coordination</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight font-display text-text-main">
              From initial upload to verified seal
            </h2>
            <p className="mt-3 text-base text-text-muted leading-relaxed">
              Every step is monitored, validated, and logged to eliminate delays and keep physicians and clinical staff aligned.
            </p>
          </div>
          <Link
            to={signedIn ? "/dashboard" : "/login"}
            className="inline-flex items-center gap-2 text-sm font-bold text-brand-600 hover:text-brand-700 group shrink-0"
          >
            <span>Experience the workflow</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              step: "01",
              title: "Upload & Index",
              text: "Coordinators attach the signed order PDF with patient reference and recipient organization NPI.",
              Icon: UploadCloud,
              badge: "Presigned S3 Upload",
            },
            {
              step: "02",
              title: "Route & Classify",
              text: "The system matches the 10-digit NPI, applies document classification, and flags potential HCPCS codes.",
              Icon: Send,
              badge: "Smart NPI Routing",
            },
            {
              step: "03",
              title: "Review & Decide",
              text: "Attending physicians inspect the PDF in-browser to approve, request a fix, or reject with notes.",
              Icon: UserCheck,
              badge: "Side-by-Side Review",
            },
            {
              step: "04",
              title: "Seal & Verify",
              text: "Approved orders receive an immutable digital seal and public QR verification link.",
              Icon: FileCheck2,
              badge: "Tamper-Proof Seal",
            },
          ].map(({ step, title, text, Icon, badge }) => (
            <article
              key={step}
              className="relative rounded-2xl bg-surface-card border border-surface-border p-6 card-shadow card-shadow-hover hover:-translate-y-1 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center font-bold">
                    <Icon size={22} />
                  </div>
                  <span className="text-3xl font-extrabold text-brand-200 font-display">{step}</span>
                </div>
                <span className="inline-block px-2.5 py-1 rounded-md bg-brand-50 text-brand-700 text-xs font-bold tracking-wide mb-2.5">
                  {badge}
                </span>
                <h3 className="text-xl font-bold font-display text-text-main">{title}</h3>
                <p className="mt-2 text-sm text-text-muted leading-relaxed font-normal">{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* 5. Role-Based Capabilities (Nurse vs Doctor Workspaces) */}
      <section id="roles" className="scroll-mt-24 bg-surface-card border-y border-surface-border py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-600">Tailored Experiences</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight font-display text-text-main">
              Purpose-built for clinical roles
            </h2>
            <p className="mt-3 text-base text-text-muted">
              Whether dispatching hundreds of intake orders or reviewing urgent charts, each persona has dedicated tools.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* Left Card: For Nurses & Document Coordinators */}
            <article className="rounded-3xl border border-brand-200 bg-brand-50/50 p-7 sm:p-9 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-brand-600 text-white flex items-center justify-center mb-6">
                  <Users size={24} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-600">
                  For Nurses & Intake Coordinators
                </span>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold font-display text-text-main">
                  Fast, accurate order dispatching
                </h3>
                <p className="mt-3 text-sm sm:text-base text-text-muted leading-relaxed">
                  Upload orders directly with automatic recipient NPI lookup, instant status tracking, and transparent fix notifications when physicians request updates.
                </p>

                <ul className="mt-6 space-y-3 text-sm font-semibold text-slate-800">
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>10-digit NPI validation prevents mistargeted deliveries</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Fix request queues highlight exactly what needs correction</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Real-time WebSocket alerts when documents are signed</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-200">
                <Link
                  to={signedIn ? "/dashboard" : "/signup/nurse"}
                  className="inline-flex items-center gap-2 text-sm font-bold text-brand-600 hover:text-brand-700"
                >
                  <span>{signedIn ? "Open Nurse Dashboard" : "Register as Nurse Coordinator"}</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </article>

            {/* Right Card: For Attending Physicians */}
            <article className="rounded-3xl bg-navy-900 text-white p-7 sm:p-9 relative overflow-hidden flex flex-col justify-between">
              <div
                className="absolute w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-50 -right-20 -top-20 pointer-events-none"
                aria-hidden="true"
              />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 text-brand-200 flex items-center justify-center mb-6">
                  <Stethoscope size={24} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-200">
                  For Attending Physicians & Medical Directors
                </span>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold font-display text-white">
                  Streamlined review & one-click sign
                </h3>
                <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed">
                  Evaluate complete clinical context without paper shuffling. Check document classifications, suggested HCPCS codes, previous revisions, and apply compliant signatures.
                </p>

                <ul className="mt-6 space-y-3 text-sm font-semibold text-white/90">
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>High-fidelity in-browser PDF viewer with page inspection</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>One-tap electronic signature with legal cryptographic binding</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Side-by-side fix notes and audit log visibility</span>
                  </li>
                </ul>
              </div>

              <div className="relative mt-8 pt-6 border-t border-white/15">
                <Link
                  to={signedIn ? "/dashboard" : "/login"}
                  className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-brand-200"
                >
                  <span>{signedIn ? "Open Doctor Queue" : "Sign in to physician queue"}</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* 6. Independent Public Seal Verification Tool */}
      <section id="verification" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="rounded-3xl bg-linear-to-br from-brand-600 to-navy-900 p-8 sm:p-12 text-white brand-cta-shadow">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-bold uppercase tracking-wider text-brand-200">
                <ShieldCheck size={15} /> Public Seal Verification
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight font-display">
                Verify any document seal in seconds.
              </h2>
              <p className="mt-3 text-base text-white/80 leading-relaxed max-w-lg">
                Third-party hospitals, payers, and compliance auditors can verify document integrity
                and signature authenticity without creating an account or logging in.
              </p>

              <form onSubmit={handleVerifySubmit} className="mt-6 flex flex-col sm:flex-row gap-2.5 max-w-md">
                <input
                  type="text"
                  placeholder="Paste seal token (e.g. 1a2b3c4d...)"
                  value={sealSearchToken}
                  onChange={(e) => setSealSearchToken(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-full bg-white/15 border border-white/25 text-white placeholder:text-white/50 text-sm outline-none focus:ring-2 focus:ring-white/40 font-mono"
                />
                <button
                  type="submit"
                  disabled={!sealSearchToken.trim()}
                  className="px-6 py-3 rounded-full bg-white text-brand-600 font-bold text-sm hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0 cursor-pointer"
                >
                  Verify seal
                </button>
              </form>
            </div>

            <div className="lg:col-span-5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-white/15 pb-3">
                <span className="text-xs uppercase font-bold tracking-wider text-brand-200">Verification Features</span>
                <QrCode size={18} className="text-brand-200" />
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Fingerprint className="text-emerald-400 shrink-0 mt-0.5" size={18} />
                  <div>
                    <strong className="block text-white">SHA-256 PDF Hash Match</strong>
                    <span className="text-xs text-white/70">Confirms the file has not been altered since sealing.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <LockKeyhole className="text-emerald-400 shrink-0 mt-0.5" size={18} />
                  <div>
                    <strong className="block text-white">X.509 Cryptographic Seal</strong>
                    <span className="text-xs text-white/70">Validates physician digital identity and signing key.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <History className="text-emerald-400 shrink-0 mt-0.5" size={18} />
                  <div>
                    <strong className="block text-white">Lifecycle Status Check</strong>
                    <span className="text-xs text-white/70">Detects superseded, expired, or revoked document states.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Security, Privacy & Enterprise Compliance */}
      <section id="security" className="scroll-mt-24 bg-surface-card border-t border-surface-border py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-600">Enterprise Standard</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight font-display text-text-main">
              Security built into every byte
            </h2>
            <p className="mt-3 text-base text-text-muted">
              Designed around strict healthcare compliance, multi-factor authentication, and tamper-resistant storage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-surface-subtle border border-surface-border">
              <div className="w-12 h-12 rounded-xl bg-white text-brand-600 border border-brand-200 flex items-center justify-center mb-4">
                <KeyRound size={22} />
              </div>
              <h3 className="text-lg font-bold font-display text-text-main">MFA by Default</h3>
              <p className="mt-2 text-sm text-text-muted leading-relaxed">
                Enforced Time-based One-Time Password (TOTP) authenticator enrollment for all clinical roles to safeguard patient health information.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-surface-subtle border border-surface-border">
              <div className="w-12 h-12 rounded-xl bg-white text-brand-600 border border-brand-200 flex items-center justify-center mb-4">
                <IdCard size={22} />
              </div>
              <h3 className="text-lg font-bold font-display text-text-main">NPI Identity Verification</h3>
              <p className="mt-2 text-sm text-text-muted leading-relaxed">
                Direct lookup against National Provider Identifier records ensures clinical accountability and valid organizational credentials.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-surface-subtle border border-surface-border">
              <div className="w-12 h-12 rounded-xl bg-white text-brand-600 border border-brand-200 flex items-center justify-center mb-4">
                <FileSignature size={22} />
              </div>
              <h3 className="text-lg font-bold font-display text-text-main">Immutable Audit Trail</h3>
              <p className="mt-2 text-sm text-text-muted leading-relaxed">
                All document uploads, reviews, fix comments, and signatures are recorded with ISO timestamps, user IDs, and cryptographic hashes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Call to Action Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="rounded-3xl bg-brand-600 p-8 sm:p-12 text-white flex flex-col lg:flex-row lg:items-center justify-between gap-8 brand-cta-shadow relative overflow-hidden">
          <div className="max-w-2xl relative z-10">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-200">
              Start Coordinating Today
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold font-display leading-tight">
              Move every clinical order forward with certainty.
            </h2>
            <p className="mt-3 text-white/80 text-base max-w-xl">
              Equip your healthcare team with a fast, verified workflow. Register using your verified NPI or access your workspace now.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 relative z-10 shrink-0">
            <Link
              to={signedIn ? "/dashboard" : "/login"}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-7 font-bold text-brand-600 hover:bg-brand-50 transition-all shadow-md"
            >
              <span>{signedIn ? "Open workspace" : "Sign in"}</span>
              <ArrowRight size={17} />
            </Link>
            {!signedIn && (
              <Link
                to="/register"
                className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/35 px-7 font-bold text-white hover:bg-white/10 transition-colors"
              >
                Register with NPI
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* 9. Comprehensive Professional Footer */}
      <footer className="border-t border-surface-border bg-surface-card text-text-muted text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="space-y-3 md:col-span-1">
              <BrandLogo className="h-9 w-auto object-contain" />
              <p className="text-text-dim leading-relaxed">
                MEATrix OrderFlow is a clinical document coordination and electronic seal platform for healthcare organizations.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-text-main uppercase tracking-wider mb-3">Product</h4>
              <ul className="space-y-2">
                <li><a href="#workflow" onClick={(e) => scrollToSection(e, "workflow")} className="hover:text-brand-600 transition-colors">Clinical Workflow</a></li>
                <li><a href="#roles" onClick={(e) => scrollToSection(e, "roles")} className="hover:text-brand-600 transition-colors">Role Worklists</a></li>
                <li><a href="#verification" onClick={(e) => scrollToSection(e, "verification")} className="hover:text-brand-600 transition-colors">Seal Verification</a></li>
                <li><Link to="/documents" className="hover:text-brand-600 transition-colors">Document Archive</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-text-main uppercase tracking-wider mb-3">Compliance & Security</h4>
              <ul className="space-y-2">
                <li><span className="text-slate-800 font-semibold">HIPAA Security Standard</span></li>
                <li><span className="text-slate-800 font-semibold">21 CFR Part 11 Electronic Records</span></li>
                <li><span className="text-slate-800 font-semibold">NPI Registry Integration</span></li>
                <li><span className="text-slate-800 font-semibold">SHA-256 Cryptographic Hash</span></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-text-main uppercase tracking-wider mb-3">Get Started</h4>
              <ul className="space-y-2">
                <li><Link to="/login" className="hover:text-brand-600 transition-colors">Physician Sign In</Link></li>
                <li><Link to="/register" className="hover:text-brand-600 transition-colors">Physician NPI Registration</Link></li>
                <li><Link to="/signup/nurse" className="hover:text-brand-600 transition-colors">Nurse Coordinator Onboarding</Link></li>
                <li><Link to="/reset-password" className="hover:text-brand-600 transition-colors">Account Recovery</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-surface-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-text-dim">
              &copy; {new Date().getFullYear()} MEATrix OrderFlow. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-text-dim">
              <span>Secure Clinical Infrastructure</span>
              <span>&bull;</span>
              <span>NPI Routing Engine</span>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
